import { getInitialAppInstallationDataUrl } from '@wix/app-installer/internal-use';
import { useEffect, useState } from 'react';
import { getErrorPropsByServerlessErrorCode, INSTALLER_ERRORS } from '../errors';
import { getHasBlocksWithoutEnforcedPermissions, getIsAdvanceOauthApp, isExternalDashboardApp } from '../selectors/appRuntimeReader';
import { useLibraryTranslations } from '../context/TranslationsContext';
import { useHandleError } from '../components/ErrorBoundary/ErrorHandlerProvider';
import { usePanorama } from '../context/PanoramaContext';
import { updateBiLoggerDefaults } from '../bi/bi';
export const usePreInstallationData = _ref => {
  let {
    appId,
    httpClient,
    tenantId,
    version,
    shareUrlId,
    preInstallationDataCache
  } = _ref;
  const {
    startTransaction,
    finishTransaction
  } = usePanorama();
  const {
    i18n
  } = useLibraryTranslations();
  const {
    handleError
  } = useHandleError();
  const [installationData, setInstallationData] = useState(undefined);
  useEffect(() => {
    if (appId && tenantId) {
      let requestId;
      startTransaction('pre-installation-data');
      (async () => {
        if (preInstallationDataCache) {
          const {
            appId: cachedAppId,
            tenantId: cachedTenantId,
            version: cachedVersion,
            preFetchPromise
          } = preInstallationDataCache;
          if (cachedAppId === appId && cachedTenantId === tenantId && cachedVersion === version) {
            return preFetchPromise;
          }
        }
        return httpClient.get(getInitialAppInstallationDataUrl({
          appId,
          version,
          tenantId,
          languageCode: i18n.language,
          shareUrlId
        }));
      })().then(res => {
        setInstallationData(res.data);
        const app = res.data.appRuntimeData;
        updateBiLoggerDefaults({
          is_advance_oauth: getIsAdvanceOauthApp(app),
          is_external_dashboard: isExternalDashboardApp(app)
        });
        requestId = res.requestId;
      }).catch(error => {
        const errorProps = getErrorPropsByServerlessErrorCode(error, INSTALLER_ERRORS.DEFAULT_FETCH_PREINSTALL_DATA);
        handleError(errorProps);
      }).finally(() => {
        finishTransaction('pre-installation-data', {
          requestId
        });
      });
    }
  }, [tenantId]);
  const app = installationData == null ? void 0 : installationData.appRuntimeData;
  return {
    app,
    isAppCompatible: (installationData == null ? void 0 : installationData.isAppCompatible) ?? true,
    hasBlocksWithoutEnforcedPermissions: getHasBlocksWithoutEnforcedPermissions(app),
    appForInstallation: installationData == null ? void 0 : installationData.appForInstallation,
    isPremiumSite: (installationData == null ? void 0 : installationData.isPremiumSite) || false,
    isSiteConnectedToDomain: (installationData == null ? void 0 : installationData.isSiteConnectedToDomain) || false,
    marketListing: installationData == null ? void 0 : installationData.marketListing,
    companyInfo: installationData == null ? void 0 : installationData.companyInfo,
    isExternalDashboard: isExternalDashboardApp(app),
    isAdvancedOauthApp: getIsAdvanceOauthApp(app),
    consentPermissions: (installationData == null ? void 0 : installationData.consentPermissions) || [],
    authorizationHeader: installationData == null ? void 0 : installationData.authorizationHeader,
    isAppAlreadyInstalled: Boolean(installationData == null ? void 0 : installationData.isAppAlreadyInstalled)
  };
};
export const emptyPreInstallationData = {
  app: undefined,
  appForInstallation: undefined,
  hasBlocksWithoutEnforcedPermissions: false,
  isSiteConnectedToDomain: false,
  isPremiumSite: false,
  companyInfo: undefined,
  marketListing: undefined,
  isExternalDashboard: false,
  isAdvancedOauthApp: false,
  authorizationHeader: undefined,
  isAppCompatible: true,
  consentPermissions: [],
  isAppAlreadyInstalled: false
};
//# sourceMappingURL=usePreInstallationData.js.map